import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LinearProgress from '@mui/material/LinearProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from "@mui/icons-material/Folder";
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useUpload } from '../../shared/context/UploadContext';
import { useAuth } from '../../shared/context/AuthContext';

const EXAM_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * @param {{ storage: import('firebase/storage').FirebaseStorage }} props
 */
export default function UploadButton({ storage }) {
    const { auth } = useAuth();
    const { isUploading, uploadPercentage, isUploadComplete, hasUploadError, triggerUpload, setIsUploadForegrounded } = useUpload();

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [selectedDirectory, setSelectedDirectory] = useState("");
    const [isDirEmpty, setIsDirEmpty] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [pendingDisplayName, setPendingDisplayName] = useState('');
    const [nameErrors, setNameErrors] = useState({ firstName: false, lastName: false });
    const [examDate, setExamDate] = useState(/** @type {import('dayjs').Dayjs | null} */ (null));
    const [examDateError, setExamDateError] = useState(false);
    const [examDateRequiredError, setExamDateRequiredError] = useState(false);
    const [SexError, setSexError] = useState(false);
    const [tumorError, setTumorError] = useState(false);
    const [sexState, setSexState] = useState('');
    const [tumorState, setTumorState] = useState(null);

    const selectedFilesRef = useRef([]);

    const resetDialog = useCallback(() => {
        selectedFilesRef.current = [];
        setIsDirEmpty(false);
        setSelectedDirectory('');
        setIsConfirming(false);
        setPendingDisplayName('');
        setNameErrors({ firstName: false, lastName: false });
        setExamDate(null);
        setExamDateError(false);
        setExamDateRequiredError(false);
        setSexError(false);
        setTumorError(false);
    }, []);

    const closeUploadDialog = useCallback(() => {
        setUploadDialogOpen(false);
        setIsUploadForegrounded(false);
        if (!isUploading) resetDialog();
    }, [isUploading, resetDialog, setIsUploadForegrounded]);

    // Clear foregrounded flag if this component unmounts while the dialog is open
    // (e.g. user navigates away mid-upload via browser back).
    useEffect(() => {
        return () => setIsUploadForegrounded(false);
    }, [setIsUploadForegrounded]);

    useEffect(() => {
        if (!isUploadComplete) return;
        if (uploadDialogOpen) closeUploadDialog();
        resetDialog();
    }, [isUploadComplete]);

    useEffect(() => {
        if (!hasUploadError) return;
        if (uploadDialogOpen) closeUploadDialog();
        resetDialog();
    }, [hasUploadError]);

    const onSelectedFolder = (files) => {
        if (files.length > 0) {
            selectedFilesRef.current = files;
            setSelectedDirectory(files[0].webkitRelativePath.split("/")[0]);
            setIsDirEmpty(false);
        } else {
            selectedFilesRef.current = [];
            setIsDirEmpty(true);
        }
    };

    const onUploadHandler = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const { firstName, lastName, sex, tumor } = Object.fromEntries(formData.entries());
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        const errors = { firstName: !trimmedFirst, lastName: !trimmedLast };
        const isSexMissing = !sex;
        const isTumorMissing = !tumor;
        const isExamDateMissing = !examDate;

        if (errors.firstName || errors.lastName || isSexMissing || isTumorMissing || isExamDateMissing) {
            setNameErrors(errors);
            setSexError(isSexMissing);
            setTumorError(isTumorMissing);
            setExamDateRequiredError(isExamDateMissing);
            return;
        }
        setPendingDisplayName(`${trimmedFirst} ${trimmedLast}`);
        setSexState(sex);
        setTumorState(tumor === 'true');
        setIsConfirming(true);
    };

    const handleConfirmUpload = () => {
        setIsConfirming(false);
        triggerUpload({
            auth,
            storage,
            files: selectedFilesRef.current,
            displayName: pendingDisplayName,
            sex: sexState,
            tumor: tumorState,
            examDate: examDate && examDate.isValid() ? examDate.format(EXAM_DATE_FORMAT) : null,
        });
    };

    return (
        <>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => { setUploadDialogOpen(true); setIsUploadForegrounded(true); }}>
                Upload Patient
            </Button>

            <Dialog open={uploadDialogOpen} onClose={closeUploadDialog} disableScrollLock>
                <DialogTitle>Upload Patient</DialogTitle>

                <DialogContent dividers>
                    {isConfirming && (
                        <DialogContentText sx={{ py: 1 }}>
                            Are you sure you want to upload <strong>{pendingDisplayName}</strong>? This cannot be undone.
                        </DialogContentText>
                    )}
                    <Box sx={{ display: isConfirming ? 'none' : 'block' }}>
                        <form onSubmit={onUploadHandler} id="upload-form">
                            <TextField
                                required
                                id="firstName"
                                name="firstName"
                                label="First name"
                                fullWidth
                                margin="normal"
                                disabled={isUploading}
                                error={nameErrors.firstName}
                                helperText={nameErrors.firstName ? 'First name is required' : ''}
                                onChange={() => setNameErrors(prev => ({ ...prev, firstName: false }))}
                            />
                            <TextField
                                required
                                id="lastName"
                                name="lastName"
                                label="Last name"
                                fullWidth
                                margin="normal"
                                disabled={isUploading}
                                error={nameErrors.lastName}
                                helperText={nameErrors.lastName ? 'Last name is required' : ''}
                                onChange={() => setNameErrors(prev => ({ ...prev, lastName: false }))}
                            />

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Exam date"
                                    value={examDate}
                                    onChange={(newValue, context) => {
                                        setExamDate(newValue);
                                        setExamDateError(Boolean(newValue) && (!newValue.isValid() || Boolean(context?.validationError)));
                                        setExamDateRequiredError(false);
                                    }}
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    disabled={isUploading}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            fullWidth: true,
                                            margin: 'normal',
                                            error: examDateError || examDateRequiredError,
                                            helperText: examDateError
                                                ? 'Enter a valid date'
                                                : (examDateRequiredError ? 'Exam date is required' : ''),
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <FormControl required error={SexError} fullWidth margin="normal">
                                <Box>
                                    <FormLabel id="sex-label">Sex</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="sex-label"
                                        name="sex"
                                        onChange={() => setSexError(false)}
                                    >
                                        <FormControlLabel value="M" control={<Radio/>} label="Male" disabled={isUploading} />
                                        <FormControlLabel value="F" control={<Radio/>} label="Female" disabled={isUploading} />
                                    </RadioGroup>
                                </Box>
                                {SexError && <FormHelperText>Sex is required</FormHelperText>}
                            </FormControl>

                            <FormControl required error={tumorError} fullWidth margin="normal">
                                <Box>
                                    <FormLabel id="tumor-label">Tumor</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="tumor-label"
                                        name="tumor"
                                        onChange={() => setTumorError(false)}
                                    >
                                        <FormControlLabel value={true} control={<Radio/>} label="Yes" disabled={isUploading} />
                                        <FormControlLabel value={false} control={<Radio/>} label="No" disabled={isUploading} />
                                    </RadioGroup>
                                </Box>
                                {tumorError && <FormHelperText>Tumor is required</FormHelperText>}
                            </FormControl>

                            <Button
                                startIcon={<FolderIcon />}
                                role={undefined}
                                tabIndex={-1}
                                component="label"
                                sx={{ mt: 2 }}
                                disabled={isUploading}
                            >
                                {selectedDirectory || 'Select folder'}
                                <input
                                    webkitdirectory=""
                                    type="file"
                                    style={{
                                        clip: 'rect(0 0 0 0)',
                                        clipPath: 'inset(50%)',
                                        height: 1,
                                        overflow: 'hidden',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        whiteSpace: 'nowrap',
                                        width: 1,
                                    }}
                                    onChange={(e) => onSelectedFolder(e.target.files)}
                                />
                            </Button>

                            <Typography hidden={!isDirEmpty} color="error">
                                Can&apos;t upload empty directory. Please select another directory.
                            </Typography>

                            {isUploading && (
                                <>
                                    <Typography sx={{ mt: 2 }} variant="body2">
                                        Uploading… {uploadPercentage}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadPercentage}
                                        sx={{ mt: 1 }}
                                    />
                                </>
                            )}
                        </form>
                    </Box>
                </DialogContent>

                <DialogActions>
                    {isConfirming ? (
                        <>
                            <Button onClick={() => setIsConfirming(false)}>Back</Button>
                            <Button onClick={handleConfirmUpload} variant="contained">Confirm upload</Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={closeUploadDialog}>
                                {isUploading ? 'Close' : 'Cancel'}
                            </Button>
                            <Button
                                type="submit"
                                form="upload-form"
                                variant="outlined"
                                disabled={!selectedDirectory || isDirEmpty || isUploading || examDateError}
                            >
                                Upload
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

UploadButton.propTypes = {
    storage: PropTypes.object.isRequired,
};
